import { Item } from "@/api";
import { formatDate, formatFloat } from "@/utils/format";
import { Column } from "primereact/column";
import { DataTable } from "primereact/datatable";
import { InputNumber } from "primereact/inputnumber";
import { useRef, useEffect, useMemo } from "react";
import { NumberWithBold } from "../types";

// 入力行用の拡張型
interface NumberWithBoldInput extends NumberWithBold {
  isInputRow?: boolean;
}

interface ItemProductionDataTableProps {
  items: Item[],
  日付: string[],
  shipments: Record<Item["品目ID"], number[]>;
  productions: Record<Item["品目ID"], NumberWithBold[]>;
  inventories: Record<Item["品目ID"], number[]>;
  compare?: boolean; // 比較モードを有効にするためのプロパティを追加
  onProductionChange?: (itemId: string, dateIndex: number, value: number, isBold: boolean) => void;
  // 各データソースを区別するための追加データ
  excelProductions?: Record<Item["品目ID"], NumberWithBold[]>; // Excel由来の生産数
  optimizerProductions?: Record<Item["品目ID"], NumberWithBold[]>; // Optimizer由来の生産数
  userProductions?: Record<Item["品目ID"], Record<number, NumberWithBold>>; // ユーザー入力の生産数
}

const ItemProductionDataTable : React.FC<ItemProductionDataTableProps> = ({
  items,
  日付,
  shipments,
  inventories,
  productions,
  compare = false,
  onProductionChange,
  excelProductions,
  optimizerProductions,
  userProductions
}) => {
  console.log("User productions:", userProductions);
  const dates = 日付
  // editableProductionsの内部状態は削除し、propsで管理されたデータを直接使用

  // 生産数変更のハンドラー（内部状態の更新は削除）
  const handleProductionChange = (itemId: string, dateIndex: number, value: number | null) => {
    const newValue = value ?? 0;

    // 親コンポーネントにのみ変更を通知
    if (onProductionChange) {
      onProductionChange(itemId, dateIndex, newValue, true);
    }
  };

  const createData = (key: "出荷" | "在庫" | "生産") => {
    const result: Record<string, string | number | NumberWithBoldInput>[] = [];

    // 各品目についてデータを作成
    items.forEach(item => {
      // 通常のデータ行
      const mainRow: Record<string, string | number | NumberWithBoldInput> = {
        '品目ID': item.品目ID,
        '品目名': item.品目名,
        'データ種別': '最新'  // データの種別を識別するためのフィールド
      };

      const inputRow: Record<string, string | number | NumberWithBoldInput> = {
        '品目ID': item.品目ID,
        '品目名': item.品目名,
        'データ種別': '入力'  // 入力行の識別用
      };

      // 比較モードかつ初期計画データが存在する場合、初期計画データ用の行も作成
      const oldRow: Record<string, string | number | NumberWithBoldInput> | null = compare ? {
        '品目ID': item.品目ID,
        '品目名': `${item.品目名} (初期)`,
        'データ種別': '初期'  // データの種別を識別するためのフィールド
      } : null;

      // 各日付のデータを追加
      dates.forEach((date, index) => {
        let value: number | NumberWithBoldInput;
        let oldValue: number | NumberWithBoldInput | undefined;

        switch(key) {
          case '出荷':
            value = shipments[item.品目ID][index];
            if (compare && shipments[`${item.品目ID}_old`]) {
              oldValue = shipments[`${item.品目ID}_old`][index];
            }
            break;
          case '在庫':
            value = inventories[item.品目ID][index];
            if (compare && inventories[`${item.品目ID}_old`]) {
              oldValue = inventories[`${item.品目ID}_old`][index];
            }
            break;
          case '生産':
            // propsから受け取った統合済みの生産データを使用
            value = productions[item.品目ID]?.[index] || { value: 0, is_bold: false };
            if (compare && productions[`${item.品目ID}_old`]) {
              oldValue = productions[`${item.品目ID}_old`][index] || { value: 0, is_bold: false };
            }
            break;
        }

        mainRow[date] = value;

        // 入力行では、ユーザー入力値があればそれを使用、なければ現在の値を使用
        if (key === '生産') {
          const userInputValue = userProductions?.[item.品目ID]?.[index];
          inputRow[date] = userInputValue ?? value;
        } else {
          inputRow[date] = value;
        }
        if (oldRow && oldValue !== undefined) {
          oldRow[date] = oldValue;
        }
      });

      // 通常データを結果に追加
      result.push(mainRow);

      // 比較モードで初期計画データがある場合は、初期計画データも追加
      if (oldRow && Object.keys(oldRow).length > 3) {
        result.push(oldRow);
      }
      if(key === '生産') {
        result.push(inputRow);
      }
    });

    return result;
  };

  const tableData = [
    { title: "生産数", data: createData('生産') },
    { title: "在庫推移", data: createData('在庫') },
    { title: "出荷予測", data: createData('出荷') },
  ];

  type DataTableRef = DataTable<Record<string, string | number | NumberWithBoldInput>[]> & { getElement: () => HTMLElement };
  const tableRef1 = useRef<DataTableRef>(null);
  const tableRef2 = useRef<DataTableRef>(null);
  const tableRef3 = useRef<DataTableRef>(null);
  const tableRefs = useMemo(() => [tableRef1, tableRef2, tableRef3], []);

  // スクロール同期処理
  useEffect(() => {
    const elements = tableRefs.map(ref => ref.current?.getElement()?.querySelector('.p-datatable-wrapper'));

    if (elements.some(el => !el)) return;

    // 各テーブルにスクロールイベントを追加
    elements.forEach((element, index) => {
      const handleScroll = () => {
        const scrollLeft = element!.scrollLeft;
        elements.forEach((el, i) => {
          if (i !== index) el!.scrollLeft = scrollLeft;
        });
      };

      element!.addEventListener('scroll', handleScroll);
    });

    // クリーンアップ
    return () => {
      elements.forEach((element, index) => {
        const handleScroll = () => {
          const scrollLeft = element!.scrollLeft;
          elements.forEach((el, i) => {
            if (i !== index) el!.scrollLeft = scrollLeft;
          });
        };

        element?.removeEventListener('scroll', handleScroll);
      });
    };
  }, [tableRefs]);

  // 行の背景色を設定するための関数
  const rowClassName = (data: Record<string, string | number | NumberWithBoldInput>) => {
    return {
      'bg-gray-100': data.データ種別 === '初期', // 初期計画データの行には背景色を設定
      'bg-blue-50': data.データ種別 === '入力' // 入力行には青色の背景色を設定
    };
  };

  // カスタムボディテンプレート関数 - 太字表示・入力フィールド用
  const cellBodyTemplate = (tableType: string) => (rowData: Record<string, string | number | NumberWithBoldInput>, options: { field: string }) => {
    const value = rowData[options.field];
    const itemId = rowData['品目ID'] as string;

    // 生産数テーブルの場合
    if (tableType === '生産数' && typeof value === 'object' && value !== null && 'value' in value) {
      const productionValue = value as NumberWithBoldInput;
      const dateIndex = dates.findIndex(date => date === options.field);

      // 通常の品目行で個別編集が可能な場合
      if (itemId && rowData['データ種別'] === '入力') {
        const dateIndex = dates.findIndex(date => date === options.field);
        // ユーザー入力値があればそれを使用、なければ元の値を使用
        const currentValue = userProductions?.[itemId]?.[dateIndex]?.value ?? productionValue.value;

        return (
          <div className="flex items-center gap-1">
            <InputNumber
              value={currentValue}
              onChange={(e) => {
                handleProductionChange(itemId, dateIndex, e.value);
              }}
              min={0}
              size={1}
              className="w-full text-xs"
            />
          </div>
        );
      }
      // 初期計画などの表示専用データ
      else {
        // データソースによってスタイルを変更
        let className = "";
        let title = "";

        // データソースの判定
        if (userProductions?.[itemId]?.[dateIndex]) {
          className = "text-blue-600 font-bold"; // ユーザー入力は青色の太字
          title = "ユーザー入力値";
        } else if (excelProductions?.[itemId]?.[dateIndex]?.is_bold) {
          className = "text-green-600 font-bold"; // Excel上のis_boldは緑色の太字
          title = "Excel固定値 (太字)";
        } else if (optimizerProductions?.[itemId]?.[dateIndex]) {
          className = productionValue.is_bold ? "font-bold" : ""; // Optimizerの結果
          title = "Optimizer結果";
        }

        return (
          <span
            className={className}
            title={title}
            style={{ fontWeight: productionValue.is_bold ? 'bold' : 'normal' }}
          >
            {productionValue.value}
          </span>
        );
      }
    }

    // それ以外の場合は通常表示
    return typeof value === 'object' && value !== null && 'value' in value
      ? (value as NumberWithBoldInput).value
      : value;
  };

  return (
    <>
      {tableData.map((item, index) => (
        <div key={index}>
          <h4>{item.title}</h4>
          <DataTable
            ref={tableRefs[index]}
            value={item.data}
            size="small"
            stripedRows
            scrollable
            scrollHeight="600px"
            rowClassName={rowClassName}
          >
            <Column field="品目名" header="品目名" style={{minWidth: '13rem'}} frozen/>
            {dates.map((date) => (
              <Column
                key={date}
                field={date}
                header={formatDate(date).replace(/^\d+\//,'')}
                style={{width: 30}}
                body={cellBodyTemplate(item.title)}
              />
            ))}
          </DataTable>
        </div>
      ))}
    </>
  )
}

export default ItemProductionDataTable
